import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, CreditCard, Check, ArrowRight } from "lucide-react";
import JunagoLogo from "./JunagoLogo";
import { openInteractiveDemo } from "./demo/DemoLauncher";

const NAV_COLUMNS = [
  {
    heading: "Produit",
    links: [
      { label: "Découvrir Junago", to: "/" },
      { label: "Solutions", to: "/solutions", testid: "footer-solutions" },
      { label: "Toutes les fonctionnalités", to: "/fonctionnalites", testid: "footer-features" },
      { label: "Tarifs", to: "/tarifs", testid: "footer-pricing" },
      { label: "Démo interactive", demo: true, testid: "footer-demo" },
    ],
  },
  {
    heading: "Solutions",
    links: [
      { label: "Gérer son activité", to: "/solutions/gerer-son-activite" },
      { label: "Vendre en ligne", to: "/solutions/vendre-en-ligne" },
      { label: "Encaisser et sécuriser", to: "/solutions/encaisser-et-securiser" },
      { label: "Piloter son entreprise", to: "/solutions/piloter-son-entreprise" },
      { label: "Automatiser avec l'IA", to: "/solutions/automatiser-avec-ia" },
      { label: "Travailler en équipe", to: "/solutions/travailler-en-equipe" },
    ],
  },
  {
    heading: "Fonctionnalités",
    links: [
      { label: "Calendrier", to: "/fonctionnalites/pilotage-calendrier" },
      { label: "Réservations", to: "/fonctionnalites/reservations-devis" },
      { label: "Site internet de réservation", to: "/fonctionnalites/page-de-reservation" },
      { label: "CRM Clients", to: "/fonctionnalites/crm-clients" },
      { label: "États des lieux", to: "/fonctionnalites/etats-des-lieux" },
      { label: "Produits & options", to: "/fonctionnalites/produits-options" },
      { label: "Revenus & facturation", to: "/fonctionnalites/revenus-facturation" },
      { label: "Paiements Stripe", to: "/fonctionnalites/encaissements" },
      { label: "Contrats électroniques", to: "/fonctionnalites/contrats-signature" },
      { label: "Emails automatiques", to: "/fonctionnalites/emails-automatiques" },
      { label: "Copilot IA", to: "/fonctionnalites/assistant-ia" },
      { label: "Gestion de flotte", to: "/fonctionnalites/flotte-entretien" },
      { label: "Paramétrages", to: "/fonctionnalites/parametrages" },
      { label: "Équipe & sécurité", to: "/fonctionnalites/equipe-securite" },
    ],
  },
  {
    heading: "Ressources",
    links: [
      { label: "Centre d'assistance", to: "/assistance", testid: "footer-help-center" },
      { label: "Documentation", to: "/assistance" },
      { label: "FAQ", to: "/assistance#faq" },
      { label: "Cas d'usage", to: "/cas-d-usage", testid: "footer-use-cases" },
      { label: "Guides", to: "/assistance" },
      { label: "Blog", soon: true },
      { label: "Comparatifs", soon: true },
      { label: "Changelog", soon: true },
    ],
  },
  {
    heading: "Entreprise",
    links: [
      { label: "Pourquoi Junago", to: "/pourquoi-junago", testid: "footer-why-junago" },
      { label: "Roadmap", to: "/assistance/roadmap", testid: "footer-roadmap" },
      { label: "Contact", to: "/contact", testid: "footer-contact" },
    ],
  },
  {
    heading: "Légal",
    links: [
      { label: "Mentions légales", to: "/legal/mentions" },
      { label: "Politique de confidentialité", to: "/legal/privacy" },
      { label: "Cookies", to: "/legal/cookies" },
      { label: "Conditions générales", to: "/legal/cgv" },
    ],
  },
];

const CTA_POINTS = [
  "Sans carte bancaire",
  "Toutes les fonctionnalités incluses",
  "Site internet de réservation inclus",
  "IA intégrée",
  "Résiliation à tout moment",
];

function FooterLink({ l }) {
  const cls = "text-slate-400 hover:text-white transition-colors duration-200";
  if (l.soon) {
    return (
      <span className="inline-flex items-center gap-2 text-slate-600 cursor-default">
        {l.label}
        <span className="px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-[9px] font-bold tracking-wide uppercase text-slate-500">Bientôt</span>
      </span>
    );
  }
  if (l.demo) return <button type="button" onClick={openInteractiveDemo} className={cls} data-testid={l.testid}>{l.label}</button>;
  if (l.href) return <a href={l.href} className={cls} data-testid={l.testid}>{l.label}</a>;
  return <Link to={l.to} className={cls} data-testid={l.testid}>{l.label}</Link>;
}

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-white mt-0" data-testid="footer">
      {/* PART 1 — Trust / Stripe */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-violet-400" />
              </div>
              <div>
                <div className="font-heading text-lg font-medium">Encaissements 100 % sécurisés</div>
                <p className="text-sm text-slate-400">Les paiements de vos clients sont protégés par Stripe.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CardBadge>Visa</CardBadge>
              <CardBadge>Mastercard</CardBadge>
              <CardBadge>CB</CardBadge>
              <div className="h-9 px-3 rounded-lg bg-[#635BFF] text-white flex items-center gap-1.5 text-xs font-bold">
                <CreditCard className="h-3.5 w-3.5" /> Stripe
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PART 2 — Hub de navigation + bloc CTA premium */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 md:py-14">
          <Link to="/" className="flex items-center w-fit" data-testid="footer-logo">
            <JunagoLogo size={34} tone="light" />
          </Link>
          <div className="mt-10 flex flex-col lg:flex-row gap-12">
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-x-6 gap-y-10">
              {NAV_COLUMNS.map((col) => (
                <div key={col.heading} data-testid={`footer-col-${col.heading.toLowerCase().replace(/[^a-z]/g, "")}`}>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-4">{col.heading}</div>
                  <ul className="space-y-2.5 text-[13px]">
                    {col.links.map((l) => (
                      <li key={l.label}><FooterLink l={l} /></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Bloc CTA premium */}
            <div className="lg:w-[320px] shrink-0">
              <div className="sheen-card relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-violet-950/70 to-[#0B0B16] p-7" data-testid="footer-cta-block">
                <div aria-hidden className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-violet-600/30 blur-3xl" />
                <div className="relative">
                  <div className="font-heading text-xl font-medium tracking-tight leading-snug">
                    Essayez Junago gratuitement pendant 30 jours
                  </div>
                  <ul className="mt-5 space-y-2.5">
                    {CTA_POINTS.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-[13px] text-slate-300">
                        <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" strokeWidth={3} /> {p}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/register?context=vendor"
                    className="btn-primary mt-6 w-full inline-flex items-center justify-center gap-2 h-11 rounded-full text-sm font-semibold transition-transform duration-300 hover:scale-[1.02]"
                    data-testid="footer-cta-start"
                  >
                    Commencer gratuitement <ArrowRight className="h-4 w-4" />
                  </Link>
                  <button
                    type="button"
                    onClick={openInteractiveDemo}
                    className="mt-2.5 w-full inline-flex items-center justify-center gap-2 h-11 rounded-full text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors"
                    data-testid="footer-cta-demo"
                  >
                    Voir la démonstration
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEO line + bottom bar */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 border-b border-white/5">
        <p className="text-xs text-slate-500 leading-relaxed max-w-4xl" data-testid="footer-seo-line">
          Junago est la plateforme tout-en-un dédiée aux professionnels de la location de véhicules.
          Gérez votre flotte, vos réservations, vos contrats, vos paiements, votre site internet de
          réservation et votre IA depuis une seule plateforme.{" "}
          <Link to="/admin/login" className="text-slate-500 hover:text-slate-300 hover:underline transition-colors" data-testid="footer-admin-link">
            • Connexion admin
          </Link>
        </p>
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <span>© {new Date().getFullYear()} Junago. Tous droits réservés.</span>
        <span>Conçu pour les loueurs professionnels, en France.</span>
      </div>
    </footer>
  );
}

function CardBadge({ children }) {
  return (
    <div className="h-9 px-3 rounded-lg bg-white/10 border border-white/10 flex items-center text-xs font-bold text-slate-300">
      {children}
    </div>
  );
}
